import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ledgerApi } from "@/api";
import { useLang } from "@/i18n";
import { PeriodFilter, yearStart, today, money } from "@/components/ledger/PeriodFilter";
import { cn } from "@/lib/utils";

export const TrialBalance = ({ onOpenAccount }) => {
  const { t } = useLang();
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [leafOnly, setLeafOnly] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    ledgerApi.trialBalance({ date_from: from || undefined, date_to: to || undefined }).then(setData).catch(() => toast.error(t("error")));
  }, [from, to, t]);

  const rows = (data?.rows || []).filter((r) => !leafOnly || r.is_leaf);

  const exportCsv = () => {
    const head = [t("account_code"), t("account_name"), t("opening"), t("debit"), t("credit"), t("closing")];
    const body = rows.map((r) => [r.code, r.name, r.opening, r.debit, r.credit, r.closing]);
    const csv = [head, ...body].map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = `isozygio_${from}_${to}.csv`; a.click();
  };

  return (
    <div className="space-y-4" data-testid="trial-balance-tab">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <PeriodFilter from={from} to={to} setFrom={setFrom} setTo={setTo} prefix="tb" />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2"><Switch checked={leafOnly} onCheckedChange={setLeafOnly} data-testid="tb-leaf-switch" /><Label className="text-sm">{t("only_leaf")}</Label></div>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="tb-export-button"><Download size={14} className="mr-1" />{t("export_csv")}</Button>
        </div>
      </div>
      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("account_code")}</th>
                <th className="px-4 py-3">{t("account_name")}</th>
                <th className="px-4 py-3 text-right">{t("opening")}</th>
                <th className="px-4 py-3 text-right">{t("debit")}</th>
                <th className="px-4 py-3 text-right">{t("credit")}</th>
                <th className="px-4 py-3 text-right">{t("closing")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
              {rows.map((r) => (
                <tr key={r.code} onClick={() => onOpenAccount(r.code)} data-testid={`tb-row-${r.code}`}
                  className={cn("cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30", !r.is_leaf && "bg-slate-50/70 dark:bg-slate-900/40 font-semibold")}>
                  <td className="px-4 py-2" style={{ paddingLeft: `${16 + r.level * 16}px` }}>{r.code}</td>
                  <td className="px-4 py-2 font-body">{r.name}</td>
                  <td className="px-4 py-2 text-right text-slate-500">{money(r.opening)}</td>
                  <td className="px-4 py-2 text-right">{money(r.debit)}</td>
                  <td className="px-4 py-2 text-right">{money(r.credit)}</td>
                  <td className={cn("px-4 py-2 text-right", r.closing < 0 ? "text-rose-700 dark:text-rose-300" : "text-emerald-700 dark:text-emerald-300")} data-testid={`tb-closing-${r.code}`}>{money(Math.abs(r.closing))}{r.closing !== 0 && (r.closing < 0 ? " Π" : " Χ")}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 font-body">{t("no_results")}</td></tr>}
            </tbody>
            {data && (
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-900/60 font-mono font-semibold border-t-2 border-slate-200 dark:border-slate-700">
                  <td className="px-4 py-3 font-body" colSpan={3}>{t("total")}</td>
                  <td className="px-4 py-3 text-right" data-testid="tb-total-debit">{money(data.totals.debit)}</td>
                  <td className="px-4 py-3 text-right" data-testid="tb-total-credit">{money(data.totals.credit)}</td>
                  <td className="px-4 py-3 text-right text-xs">{t("closing_debit")} {money(data.totals.closing_debit)} · {t("closing_credit")} {money(data.totals.closing_credit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};
